import { useCallback, useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/SidePanel";
import {
  Radar, Loader2, Clock, MapPin, GitBranch, FlaskConical, AlertTriangle,
  ShieldCheck, Lock, XCircle, CheckCircle2, CircleDashed, Trophy, BadgeCheck, ExternalLink,
  FileText, Download, FileSpreadsheet,
} from "lucide-react";
import { api } from "@/lib/api";
import { HAZARD_BASE, HAZARD_LABEL } from "@/lib/hazard";
import { fmtAbs } from "@/lib/time";
import { SatelliteImagery, BeforeAfterImagery } from "@/components/SatelliteImagery";
import { AccuracyBlock, RegionTrackRecord } from "@/components/BriefingAccuracy";

const pct = (v) => (v == null ? "—" : `${Math.round(v * 100)}%`);

const confTone = (c) =>
  c >= 0.7 ? "text-atlas-red" : c >= 0.6 ? "text-atlas-orange" : "text-amber-300";

const SignalChip = ({ s, rejected }) => {
  const color = rejected ? "#6B7280" : HAZARD_BASE[s.hazard] || "#00F0FF";
  const label = s.kind === "glof_watch" ? "GLOF" : HAZARD_LABEL[s.hazard] || s.hazard;
  const sig = typeof s.sigma === "number" ? ` ${s.sigma >= 0 ? "+" : ""}${s.sigma.toFixed(1)}σ` : "";
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 border text-[9px] font-mono ${rejected ? "line-through opacity-70" : ""}`}
      style={{ borderColor: `${color}55`, color }}
      title={rejected ? s.reason || "rejected" : s.region}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: color }} />
      {label}{sig}
    </span>
  );
};

// Confidence + the life-critical projected window, grouped and given strong visual weight.
const ForecastMeter = ({ item }) => (
  <div className="text-right shrink-0 min-w-[104px]">
    <div className={`text-2xl font-mono leading-none ${confTone(item.confidence)}`}>{pct(item.confidence)}</div>
    <div className="text-[8px] font-mono uppercase tracking-widest text-atlas-secondary/75 mb-1.5">confidence</div>
    <div
      className="inline-flex items-center gap-1 border border-atlas-cyan/50 bg-atlas-cyan/10 px-1.5 py-0.5"
      data-testid={`foresight-window-${item.uid}`}
    >
      <Clock className="w-3 h-3 text-atlas-cyan" />
      <span className="text-[13px] font-mono font-bold tracking-tight text-atlas-cyan leading-none">
        {item.prediction_window || item.projected_window}
      </span>
    </div>
    <div className="text-[8px] font-mono uppercase tracking-widest text-atlas-cyan/60 mt-0.5">projected window</div>
  </div>
);

const ForesightCard = ({ item, bulletin, report, ledger }) => {
  const used = item.signals_used || item.contributing_signals || [];
  const rejected = item.signals_rejected || [];
  return (
    <div
      data-testid={`foresight-${bulletin ? "bulletin" : "watch"}-${item.uid}`}
      className={`border p-3 ${bulletin ? "border-atlas-red/40 bg-atlas-red/[0.05]" : "border-white/10 bg-white/[0.02]"}`}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            {bulletin ? (
              <AlertTriangle className="w-3.5 h-3.5 text-atlas-red shrink-0" />
            ) : (
              <GitBranch className="w-3.5 h-3.5 text-atlas-cyan/70 shrink-0" />
            )}
            <span className="text-[13px] font-display text-white leading-tight">{item.name}</span>
            {item.experimental && (
              <span className="inline-flex items-center gap-0.5 text-[8px] font-mono uppercase tracking-wider text-amber-300/80 border border-amber-300/30 px-1">
                <FlaskConical className="w-2.5 h-2.5" /> experimental
              </span>
            )}
          </div>
          <div className="flex items-center gap-1 text-[10px] font-mono text-atlas-secondary/85 mt-0.5">
            <MapPin className="w-3 h-3" /> {item.region}
            {item.near_tectonic && <span className="text-atlas-secondary/75">· tectonic boundary</span>}
          </div>
        </div>
        <ForecastMeter item={item} />
      </div>

      <div className="mb-1.5">
        <div className="text-[8px] font-mono uppercase tracking-widest text-atlas-secondary/75 mb-1">Signals used</div>
        <div className="flex flex-wrap items-center gap-1.5">
          {used.map((s, i) => <SignalChip key={i} s={s} />)}
        </div>
      </div>

      {rejected.length > 0 && (
        <div className="mb-2" data-testid={`foresight-rejected-${item.uid}`}>
          <div className="flex items-center gap-1 text-[8px] font-mono uppercase tracking-widest text-atlas-secondary/75 mb-1">
            <ShieldCheck className="w-2.5 h-2.5" /> Signals rejected (temporal firewall)
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            {rejected.map((s, i) => <SignalChip key={i} s={s} rejected />)}
          </div>
          <p className="text-[9px] font-mono text-atlas-secondary/75 mt-1 leading-relaxed">
            {rejected[0]?.reason}
          </p>
        </div>
      )}

      <p className="text-[11px] text-atlas-secondary leading-relaxed mb-2">{item.rationale}</p>

      {item.imagery && <SatelliteImagery imagery={item.imagery} testId={`foresight-imagery-${item.uid}`} compact />}

      <div className="flex items-center justify-between text-[10px] font-mono border-t border-white/5 pt-1.5 gap-2 mt-2">
        <span className="inline-flex items-center gap-1 text-atlas-secondary/80" title="Information cutoff — only data on or before this time was used">
          <Lock className="w-3 h-3" /> cutoff {fmtAbs(item.data_cutoff)}
        </span>
        <span className="text-atlas-secondary/80 text-right">
          onset by ~{fmtAbs(item.expires_at || item.projected_onset)} ·{" "}
          <span className="uppercase text-atlas-secondary/80">{HAZARD_LABEL[item.outcome_hazard] || item.outcome_hazard}</span>
        </span>
      </div>

      {bulletin && (
        <>
          <AccuracyBlock report={report} hazard={item.outcome_hazard} testId={`foresight-accuracy-${item.uid}`} />
          <RegionTrackRecord ledger={ledger} region={item.region} testId={`foresight-trackrecord-${item.uid}`} />
        </>
      )}
    </div>
  );
};

const OUTCOME = {
  PENDING: { icon: CircleDashed, cls: "text-amber-300 border-amber-300/40 bg-amber-300/10", label: "PENDING" },
  CONFIRMED: { icon: CheckCircle2, cls: "text-atlas-red border-atlas-red/50 bg-atlas-red/10", label: "CONFIRMED" },
  EXPIRED: { icon: XCircle, cls: "text-atlas-secondary/80 border-white/15 bg-white/[0.03]", label: "EXPIRED" },
};

const OutcomeBadge = ({ outcome }) => {
  const o = OUTCOME[outcome] || OUTCOME.PENDING;
  const Icon = o.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 border text-[9px] font-mono uppercase tracking-wider ${o.cls}`}>
      <Icon className="w-2.5 h-2.5" /> {o.label}
    </span>
  );
};

const VerifiedSeal = ({ ev, uid }) => {
  const src = (ev.source || "agency").toUpperCase();
  const label = src.includes("GDACS") ? "GDACS" : src;
  const Wrap = ev.link ? "a" : "div";
  const wrapProps = ev.link ? { href: ev.link, target: "_blank", rel: "noreferrer" } : {};
  return (
    <Wrap
      {...wrapProps}
      data-testid={`foresight-verified-${uid}`}
      className={`mt-2 flex items-center justify-between gap-2 border border-emerald-400/40 bg-emerald-400/[0.07] px-2 py-1.5 group transition-colors ${ev.link ? "cursor-pointer hover:bg-emerald-400/[0.14]" : ""}`}
    >
      <span className="inline-flex items-center gap-1.5 min-w-0">
        <BadgeCheck className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
        <span className="text-[9px] font-mono uppercase tracking-wider text-emerald-300 shrink-0">
          Verified · {label}
        </span>
        <span className="text-[10px] font-mono text-atlas-secondary/85 truncate">
          {ev.title}{ev.observed_at ? ` · ${fmtAbs(ev.observed_at)}` : ""}
        </span>
      </span>
      <span className="inline-flex items-center gap-1.5 shrink-0">
        {ev.distance_km != null && (
          <span className="text-[9px] font-mono text-atlas-secondary/75">~{Math.round(ev.distance_km)} km</span>
        )}
        {ev.link && <ExternalLink className="w-3 h-3 text-emerald-400/70 group-hover:text-emerald-300" />}
      </span>
    </Wrap>
  );
};

const LockedRow = ({ b }) => (
  <div
    data-testid={`foresight-ledger-${b.uid}`}
    className="border border-white/10 bg-white/[0.02] p-2.5"
  >
    <div className="flex items-start justify-between gap-2 mb-1">
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <Lock className="w-3 h-3 text-atlas-secondary/80 shrink-0" />
          <span className="text-[12px] font-display text-white leading-tight truncate">{b.name}</span>
        </div>
        <div className="text-[10px] font-mono text-atlas-secondary/85 mt-0.5">{b.region}</div>
      </div>
      <OutcomeBadge outcome={b.outcome} />
    </div>
    <div className="flex items-center gap-3 text-[10px] font-mono mt-1.5">
      <span className={`${confTone(b.confidence)}`}>{pct(b.confidence)}</span>
      <span className="inline-flex items-center gap-1 text-atlas-cyan font-bold">
        <Clock className="w-3 h-3" /> {b.prediction_window}
      </span>
      <span className="text-atlas-secondary/75">envelope ~{Math.round(b.geographic_envelope_km)} km</span>
    </div>
    <div className="flex items-center justify-between text-[9px] font-mono text-atlas-secondary/75 mt-1.5 pt-1.5 border-t border-white/5">
      <span className="inline-flex items-center gap-1"><Lock className="w-2.5 h-2.5" /> cutoff {fmtAbs(b.data_cutoff)}</span>
      <span>{(b.signals_used || []).length} used · {(b.signals_rejected || []).length} rejected</span>
    </div>
    {b.outcome === "CONFIRMED" && b.confirmed_by && <VerifiedSeal ev={b.confirmed_by} uid={b.uid} />}
    {b.before_after && <BeforeAfterImagery pair={b.before_after} testId={`foresight-beforeafter-${b.uid}`} />}
    {b.imagery && !b.before_after && <SatelliteImagery imagery={b.imagery} testId={`foresight-ledger-imagery-${b.uid}`} compact />}
  </div>
);

const VALIDATION_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/validation/report`;

const ValidationReport = () => {
  const [ver, setVer] = useState(null);
  useEffect(() => {
    fetch(VALIDATION_BASE)
      .then((r) => r.json())
      .then((d) => setVer(d?.meta?.report_version || null))
      .catch(() => {});
  }, []);
  return (
  <div className="border border-emerald-400/25 bg-emerald-400/[0.04] p-3" data-testid="validation-report">
    <div className="flex items-center justify-between gap-2 mb-1">
      <div className="flex items-center gap-1.5">
        <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
        <span className="text-[10px] uppercase tracking-widest text-emerald-300/80 font-mono">
          Validation & Track-Record Report
        </span>
      </div>
      {ver && (
        <span className="text-[8px] font-mono text-emerald-300/60 border border-emerald-400/20 px-1.5 py-0.5"
              data-testid="validation-version" title="Auto-refreshed & version-stamped daily">
          {ver}
        </span>
      )}
    </div>
    <p className="text-[10px] font-mono text-atlas-secondary/80 leading-relaxed mb-2.5">
      Independent, honest metrics computed from Vigilo Earth's own immutable ledger — per-hazard hit-rates,
      Brier skill + discrimination (AUC), tamper-evidence audit, methodology & full data provenance.
      Auto-refreshed and version-stamped daily. Due-diligence grade.
    </p>
    <div className="flex flex-wrap items-center gap-2">
      <a
        href={`${VALIDATION_BASE}.pdf`} target="_blank" rel="noreferrer"
        data-testid="download-validation-pdf"
        className="inline-flex items-center gap-1.5 border border-emerald-400/50 bg-emerald-400/10 hover:bg-emerald-400/20 px-2.5 py-1.5 text-[11px] font-mono text-emerald-200 transition-colors"
      >
        <FileText className="w-3.5 h-3.5" /> Download PDF
      </a>
      <a
        href={`${VALIDATION_BASE}.csv`} target="_blank" rel="noreferrer"
        data-testid="download-validation-csv"
        className="inline-flex items-center gap-1.5 border border-white/15 bg-white/[0.03] hover:bg-white/[0.07] px-2.5 py-1.5 text-[11px] font-mono text-atlas-secondary transition-colors"
      >
        <FileSpreadsheet className="w-3.5 h-3.5" /> CSV
      </a>
      <a
        href={VALIDATION_BASE} target="_blank" rel="noreferrer"
        data-testid="download-validation-json"
        className="inline-flex items-center gap-1.5 border border-white/15 bg-white/[0.03] hover:bg-white/[0.07] px-2.5 py-1.5 text-[11px] font-mono text-atlas-secondary transition-colors"
      >
        <Download className="w-3.5 h-3.5" /> JSON
      </a>
    </div>
  </div>
  );
};

const SkillBoard = ({ skill }) => {
  if (!skill) return null;
  const bl = skill.bulletins || {};
  const pr = skill.predictions || {};
  const cal = skill.calibration || {};
  const pctOrDash = (v) => (v == null ? "—" : `${Math.round(v * 100)}%`);
  const bss = cal.brier_skill;
  const auc = cal.auc;
  return (
    <div className="border border-atlas-cyan/20 bg-atlas-cyan/[0.04] p-3" data-testid="foresight-skillboard">
      <div className="flex items-center gap-1.5 mb-2">
        <Trophy className="w-3.5 h-3.5 text-atlas-cyan" />
        <span className="text-[10px] uppercase tracking-widest text-atlas-cyan/70 font-mono">
          Foresight Skill Board — track record vs reality
        </span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="border border-white/10 bg-atlas-void/50 p-2">
          <div className="text-lg font-mono text-white leading-none">{pctOrDash(bl.hit_rate)}</div>
          <div className="text-[8px] font-mono uppercase tracking-widest text-atlas-secondary/75 mt-0.5">bulletin hit-rate</div>
          <div className="text-[9px] font-mono text-atlas-secondary/80 mt-0.5">
            {bl.confirmed || 0} hit · {bl.expired || 0} miss · {bl.pending || 0} pending
          </div>
        </div>
        <div className="border border-white/10 bg-atlas-void/50 p-2">
          <div className="text-lg font-mono text-white leading-none">{pctOrDash(pr.hit_rate)}</div>
          <div className="text-[8px] font-mono uppercase tracking-widest text-atlas-secondary/75 mt-0.5">prediction hit-rate</div>
          <div className="text-[9px] font-mono text-atlas-secondary/80 mt-0.5">{pr.hits || 0} / {pr.resolved || 0} resolved</div>
        </div>
        <div className="border border-white/10 bg-atlas-void/50 p-2">
          <div className={`text-lg font-mono leading-none ${bss == null ? "text-atlas-secondary/80" : bss > 0 ? "text-emerald-400" : "text-amber-300"}`}>
            {bss == null ? "—" : (bss > 0 ? "+" : "") + bss.toFixed(2)}
          </div>
          <div className="text-[8px] font-mono uppercase tracking-widest text-atlas-secondary/75 mt-0.5">brier skill</div>
          <div className="text-[9px] font-mono text-atlas-secondary/80 mt-0.5">
            vs climatology · Brier {cal.brier == null ? "—" : cal.brier.toFixed(3)}
          </div>
        </div>
        <div className="border border-white/10 bg-atlas-void/50 p-2">
          <div className={`text-lg font-mono leading-none ${auc == null ? "text-atlas-secondary/80" : auc >= 0.6 ? "text-emerald-400" : "text-atlas-secondary/80"}`}>
            {auc == null ? "—" : auc.toFixed(2)}
          </div>
          <div className="text-[8px] font-mono uppercase tracking-widest text-atlas-secondary/75 mt-0.5">discrimination (AUC)</div>
          <div className="text-[9px] font-mono text-atlas-secondary/80 mt-0.5">
            0.5 = none · {cal.maturity || "prior"}
          </div>
        </div>
      </div>
      <p className="text-[8px] font-mono text-atlas-secondary/75 mt-1.5 leading-relaxed">
        Metrics are recomputed leave-one-out from resolved outcomes. Brier skill is scored vs a hazard-agnostic
        climatology (positive = Vigilo Earth separates high- vs low-risk hazards); within-hazard discrimination (AUC)
        is reported transparently and is still maturing. No inflation.
      </p>
    </div>
  );
};

export const ForesightPanel = ({ open, onOpenChange }) => {
  const [data, setData] = useState(null);
  const [report, setReport] = useState(null);
  const [skill, setSkill] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [f, cal, sk] = await Promise.all([
        api.foresight(),
        api.calibration().catch(() => null),
        api.foresightSkill().catch(() => null),
      ]);
      setData(f);
      setReport(cal);
      setSkill(sk);
    } catch {
      setData({ bulletins: [], watchlist: [], ledger: [] });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const bulletins = data?.bulletins || [];
  const watchlist = data?.watchlist || [];
  const ledger = data?.ledger || [];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" aria-describedby={undefined} className="w-full sm:max-w-lg bg-atlas-void border-l border-white/10 overflow-y-auto p-0" data-testid="foresight-panel">
        <SheetHeader className="p-4 border-b border-white/10 sticky top-0 bg-atlas-void z-10">
          <SheetTitle className="flex items-center gap-2 text-white font-display">
            <Radar className="w-4 h-4 text-atlas-red" /> Event Foresight
          </SheetTitle>
          <p className="text-[11px] font-mono text-atlas-secondary/85 leading-relaxed mt-1">
            Cross-layer precursor synthesis under a strict <span className="text-atlas-cyan">temporal firewall</span> —
            only information on or before each bulletin's data cutoff is used, and any signal describing the outcome
            is rejected. Flags the risk <span className="text-atlas-cyan">before</span> the event.
          </p>
        </SheetHeader>

        <div className="p-4 space-y-4">
          {loading && (
            <div className="flex items-center gap-2 text-atlas-secondary text-sm font-mono">
              <Loader2 className="w-4 h-4 animate-spin" /> Synthesising precursors…
            </div>
          )}

          {!loading && (
            <>
              <ValidationReport />
              <SkillBoard skill={skill} />
              <div>
                <div className="text-[10px] uppercase tracking-widest text-atlas-red/80 font-mono mb-2">
                  Foresight Bulletins · ≥70% ({bulletins.length})
                </div>
                {bulletins.length === 0 ? (
                  <div className="text-[11px] font-mono text-atlas-secondary/80 border border-white/10 p-3">
                    No precursor pattern currently clears the 70% bulletin threshold. Vigilo Earth keeps hunting every cycle.
                  </div>
                ) : (
                  <div className="space-y-2.5" data-testid="foresight-bulletins">
                    {bulletins.map((b) => <ForesightCard key={b.uid} item={b} bulletin report={report} ledger={ledger} />)}
                  </div>
                )}
              </div>

              {watchlist.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-atlas-cyan/70 font-mono mb-2">
                    Cross-Layer Watchlist · 50–70% ({watchlist.length})
                  </div>
                  <div className="space-y-2.5" data-testid="foresight-watchlist">
                    {watchlist.map((w) => <ForesightCard key={w.uid} item={w} />)}
                  </div>
                </div>
              )}

              {ledger.length > 0 && (
                <div>
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-atlas-secondary/85 font-mono mb-2">
                    <Lock className="w-3 h-3" /> Locked Bulletin Ledger — Track Record ({ledger.length})
                  </div>
                  <div className="space-y-2" data-testid="foresight-ledger">
                    {ledger.map((b) => <LockedRow key={b.uid} b={b} />)}
                  </div>
                </div>
              )}

              <p className="text-[9px] font-mono text-atlas-secondary/75 leading-relaxed pt-2">
                Every bulletin is permanently stored and locked at generation — data cutoff, projected window,
                expected event, geographic envelope, confidence and signals used/rejected — with its outcome
                resolved later against reality. Bulletins are track-able precursor hypotheses, not deterministic
                predictions. Experimental couplings (e.g. seismo-atmospheric / LAIC) are flagged research-grade.
              </p>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
